import { shallowRef } from "vue";

import { boardAspect } from "@/model/Settings";
import type { ZoomedRectangle } from "@/model/board";
import type {
  BoardCoordinate,
  RelativeCoordinate,
  SizedRectangle,
  ViewportCoordinate,
  WindowCoordinate,
  WindowLen,
} from "@/model/coordinates";
import { windowLen } from "@/model/coordinates";
import { useBoardSizeStore } from "@/store/boardSize";
import { useSidePanelStore } from "@/store/sidePanel";
import { useZoomStore } from "@/store/zoom";
import {
  clientCoord,
  clientSize,
  contentSize,
  scrollPos,
  scrollSize,
  windowFromViewport,
} from "@/utils/dom/coordinates";

import { boardScaleFromWindow, relativeFromWindow, scrollDiff } from "./coordinate-transform";
import { clampCoord, divided, isEqual, minCoord, minus, plus, times } from "./coordinates";

/**
 * If the card is outside the board/viewport, moves it back inside the board.
 * This modifies the first input parameter (pos) in place.
 * If 3rd arg (checkViewport) is true, the card is also moved back inside the viewport
 *
 * @returns The offset by which the card was moved
 */
export function validateCardPositionInBoard(
  pos: BoardCoordinate,
  size: BoardCoordinate,
  checkViewport: boolean,
): BoardCoordinate {
  const original = { ...pos };
  const boardRect = useBoardSizeStore().board;
  const zoom = useZoomStore().factor;

  // card inside board
  const boardSize = boardScaleFromWindow(boardRect.size);
  const overlap = minus(plus(pos, size), boardSize);
  if (pos.x < 0) {
    pos.x = 0;
  }
  if (overlap.x > 0) {
    pos.x -= overlap.x;
  }
  if (pos.y < 0) {
    pos.y = 0;
  }
  if (overlap.y > 0) {
    pos.y -= overlap.y;
  }

  if (!checkViewport) {
    return minus(pos, original);
  }

  const outline = 2; // the outline of the sticky note
  const gap = 2; // desired gap between sticky note and viewport
  const topMargin = 48; // space for the menu

  const visible = visibleWindowRect();
  const boardOffset = minus(smoothScrollPos.value ?? scrollPos(), boardRect.p0);
  const inset = outline + gap;
  const viewportLeftTop = plus(plus(visible.p0, boardOffset), windowLen(inset, inset + topMargin));
  const viewportRightBottom = minus(plus(visible.p1, boardOffset), windowLen(inset, inset));

  const overlapLeftTop = minus(times(pos, zoom), boardScaleFromWindow(viewportLeftTop));
  const overlapRightBottom = minus(
    times(plus(pos, size), zoom),
    boardScaleFromWindow(viewportRightBottom),
  );
  if (overlapLeftTop.x < 0) {
    pos.x -= overlapLeftTop.x / zoom;
  }
  if (overlapRightBottom.x > 0) {
    pos.x -= overlapRightBottom.x / zoom;
  }

  if (overlapLeftTop.y < 0) {
    pos.y -= overlapLeftTop.y / zoom;
  }
  if (overlapRightBottom.y > 0) {
    pos.y -= overlapRightBottom.y / zoom;
  }

  return minus(pos, original);
}

export function calcBoardRect(zoom = useZoomStore().factor): ZoomedRectangle {
  const margin = useBoardSizeStore().leftTopMargin;
  const windowSize = minus(visibleWindowSize(), useBoardSizeStore().totalVisibleMargin);
  const boardSpace = minus(windowSize, useBoardSizeStore().totalScrollableMargin);
  if (windowSize.x / windowSize.y < boardAspect) {
    // window is higher than boardAspect -> make board less high, add margin at top
    const size = windowLen(windowSize.x, windowSize.x / boardAspect);
    const shortened = windowLen(0, Math.max(0, (boardSpace.y - size.y * zoom) / 2));
    return { zoom, p0: plus(shortened, margin), size };
  }
  // window is broader than boardAspect -> make board less broad, add margin at left
  const size = windowLen(windowSize.y * boardAspect, windowSize.y);
  const shortened = windowLen(Math.max(0, (boardSpace.x - size.x * zoom) / 2), 0);
  return { zoom, p0: plus(shortened, margin), size };
}

/**
 * Adjust useBoardSizeStore().scrollableMargin so that
 * there is enough space for a side panel of given size without the board scrolling
 */
export function adjustBoardLeftSize(left: number) {
  // compute the rect fresh: useBoardSizeStore().board lags one flush behind, so a
  // release and a reserve in the same tick (panel handover) would both see the
  // pre-release value and the reserve would end up reserving nothing
  const board = calcBoardRect();
  const neededLeftSize = left - board.p0.x;
  if (neededLeftSize > 0) {
    assureBoardLeftSize(left, board, board, 1);
  } else {
    // release the left side; the right one too, unless an open right panel holds it
    // (growing the left margin also grew the right one, see assureBoardLeftSize)
    removeBoardScrollMargin(windowLen(-neededLeftSize, 0), releaseCap("right"));
  }
}

/**
 * Mirror of adjustBoardLeftSize for a right-side panel.
 * Only rightBottomMargin creates scroll room — the gap at full scroll is
 * `rightBottomMargin.x - panelWidth`. Free gutter beside a centered board
 * does not count; it lies underneath the panel.
 */
export function adjustBoardRightSize(right: number) {
  const neededRightSize = right - useBoardSizeStore().rightBottomMargin.x;
  if (neededRightSize > 0) {
    // read maxScrollX before growing the right margin, since it depends on it
    const centered = maxScrollX(calcBoardRect()) === 0;
    const add = windowLen(Math.ceil(neededRightSize), 0);
    useBoardSizeStore().addScrollableRightBottom(add);
    if (centered) {
      // no scroll bars -> window wider than content -> board is centered -> add the
      // same space to the left so the board keeps its position
      useBoardSizeStore().addScrollableLeftTop(add);
    }
  } else {
    // release the right side; the left one too, unless an open left panel holds it
    // (growing the right margin also grew the left one, see assureBoardRightSize)
    removeBoardScrollMargin(releaseCap("left"), windowLen(-neededRightSize, 0));
  }
}

// how much of a side's margin a panel on the OTHER side may give back when it
// closes: nothing while an open panel holds the side, everything otherwise
function releaseCap(side: "left" | "right") {
  return useBoardSizeStore().marginLockedSides[side] ? windowLen(0, 0) : undefined;
}

/** Give back scrollable margin, at most the given cap per side (no cap = as much as possible). */
export function removeBoardScrollMargin(leftTopCap?: WindowLen, rightBottomCap?: WindowLen) {
  const docElem = document.documentElement;
  const maxScroll = minus(scrollSize(docElem), clientSize(docElem));
  const possibleLeftTop = minCoord([
    useBoardSizeStore().scrollableMargin.p0,
    scrollPos(),
    leftTopCap || windowLen(Infinity, Infinity),
  ]);
  const possibleRightBottom = minCoord([
    useBoardSizeStore().scrollableMargin.p1,
    minus(maxScroll, scrollPos()),
    rightBottomCap || windowLen(Infinity, Infinity),
  ]);
  if (
    possibleLeftTop.x !== 0 ||
    possibleLeftTop.y !== 0 ||
    possibleRightBottom.x !== 0 ||
    possibleRightBottom.y !== 0
  ) {
    useBoardSizeStore().addScrollableLeftTop(minus(possibleLeftTop));
    useBoardSizeStore().addScrollableRightBottom(minus(possibleRightBottom));
  }
}

function assureBoardLeftSize(
  size: number,
  initial: SizedRectangle,
  current: SizedRectangle,
  iter: number,
) {
  // endless loops should not happen, but better make sure
  if (iter > 3) {
    return;
  }
  const addLeft = Math.ceil(size - current.p0.x);
  if (addLeft >= 1) {
    useBoardSizeStore().addScrollableLeftTop(windowLen(addLeft, 0));
    if (maxScrollX(current) === 0) {
      // no scroll bars -> window wider than content -> board is centered -> add same space also to the right
      useBoardSizeStore().addScrollableRightBottom(windowLen(addLeft, 0));
    }
    return assureBoardLeftSize(size, initial, calcBoardRect(), iter + 1);
  }
  // assure there's enough space to the right so that we can scroll the board
  // the amount we added to the left (so it does not move visibly)
  const needScroll = plus(scrollPos(), minus(current.p0, initial.p0)).x;
  const addRight = Math.floor(needScroll) - Math.ceil(maxScrollX(current));
  if (addRight > 0) {
    useBoardSizeStore().addScrollableRightBottom(windowLen(addRight, 0));
  }
}

function maxScrollX(rect: SizedRectangle) {
  const contentWidth =
    rect.p0.x + rect.size.x * useZoomStore().factor + useBoardSizeStore().rightBottomMargin.x;
  return Math.max(0, contentWidth - document.documentElement.clientWidth);
}

export function relativeFromViewport(c: ViewportCoordinate): RelativeCoordinate {
  return relativeFromWindow(windowFromViewport(c));
}

export function relativeClientCoord(elem: MouseEvent): RelativeCoordinate {
  return relativeFromViewport(clientCoord(elem));
}

export function visibleWindowSize() {
  return minus(contentSize(), useBoardSizeStore().totalWindowMargin);
}

export function visibleCenterCoord(): ViewportCoordinate {
  const { p0, p1 } = visibleWindowRect();
  return divided(plus(p0, p1), 2) as unknown as ViewportCoordinate;
}

function visibleWindowRect() {
  const { left, right } = useSidePanelStore();
  const { windowMargin } = useBoardSizeStore();
  return {
    p0: plus(windowMargin.p0, windowLen(left.width, 0)),
    p1: minus(contentSize(), plus(windowMargin.p1, windowLen(right.width, 0))),
  };
}

// scroll the window so that the window and the viewport coordinates are equal
export function scrollTo(w: WindowCoordinate, v: ViewportCoordinate) {
  const s = scrollDiff(w, v);
  window.scrollTo(s.x, s.y);
}

const smoothScrollPos = shallowRef<WindowLen | null>(null);

export function scrollSmoothTo(w: WindowCoordinate, v: ViewportCoordinate, clampToTarget: boolean) {
  const target = reachableScroll(scrollDiff(w, v));
  if (isEqual(target, scrollPos(), 1)) return;

  const track = () => (smoothScrollPos.value = clampToTarget ? target : scrollPos());
  track();
  window.addEventListener("scroll", track);
  window.addEventListener(
    "scrollend",
    () => {
      window.removeEventListener("scroll", track);
      smoothScrollPos.value = null;
    },
    { once: true },
  );
  window.scrollTo({ left: target.x, top: target.y, behavior: "smooth" });
}

function reachableScroll(diff: WindowLen) {
  const maxScroll = minus(scrollSize(document.documentElement), contentSize());
  return clampCoord(diff, windowLen(0, 0), maxScroll);
}

const offScreen = -10;

export function offScreenCoord<T extends ViewportCoordinate | RelativeCoordinate>() {
  return { x: offScreen, y: offScreen } as T;
}

export function isOffScreen(c: ViewportCoordinate | RelativeCoordinate) {
  return c.x === offScreen && c.y === offScreen;
}
